import { makeAutoObservable, runInAction } from 'mobx';
import TextBlockDataHelpers from '@nedoShared/modules/notes/block/textBlock/helpers/TextBlock.data.helpers';
import TextBlockConstants from '@nedoShared/modules/notes/block/textBlock/TextBlock.constants';
const { SLASH } = TextBlockConstants;
const { parseText, defineElementData, convertTextListToObjectList, convertObjectListToText, newText, newInput, compare, } = TextBlockDataHelpers;
class TextBlockStore {
    constructor(text, data) {
        this.text = '';
        this.data = {};
        this.elements = [];
        this.text = text;
        this.data = data;
        this.elements = defineElementData(convertTextListToObjectList(parseText(text)), data);
        makeAutoObservable(this);
    }
    UPDATE_ELEMENTS(text, data) {
        this.text = text;
        this.data = data;
        this.elements = defineElementData(convertTextListToObjectList(parseText(text)), data);
    }
    UPDATE_ELEMENTS_DATA(text, data) {
        const result = compare(this.elements, data);
        if (result.hasNewData) {
            this.UPDATE_ELEMENTS(text, data);
        }
        else if (result.changedElementIds.length) {
            result.changedElementIds.forEach((id) => {
                const element = this.elementsMap[id];
                if (element && element.macroData.type !== 'text') {
                    runInAction(() => {
                        element.macroData = data[element.macroId];
                    });
                }
            });
        }
    }
    EDIT_INPUT_ELEMENT(elementId, newValues) {
        const element = this.elementsMap[elementId];
        for (const key of Object.keys(newValues)) {
            element[key] = newValues[key];
        }
    }
    EDIT_TEXT_ELEMENT(elementId, newValues) {
        const element = this.elementsMap[elementId];
        for (const key of Object.keys(newValues)) {
            element[key] = newValues[key];
        }
    }
    EDIT_PLACEHOLDER_ELEMENT(elementId, newMacroData) {
        const element = this.elementsMap[elementId];
        for (const key of Object.keys(newMacroData)) {
            element.macroData[key] = newMacroData[key];
        }
    }
    // computed
    get elementsMap() {
        const result = {};
        this.elements.forEach((element) => {
            result[element.id] = element;
        });
        return result;
    }
    get updatedText() {
        return convertObjectListToText(this.elements);
    }
    get updatedTextOnly() {
        return this.elements
            .filter((element) => element.type === 'text' || element.type === 'placeholder')
            .reduce((result, item) => result + item.value, '');
    }
    // actions
    getRange(start, end) {
        let startIndex = 0;
        let endIndex = 0;
        this.elements.forEach((element, index) => {
            if (element.id === start.elementId) {
                startIndex = index;
            }
            if (element.id === end.elementId) {
                endIndex = index;
            }
        });
        if (startIndex === endIndex) {
            return;
        }
        else if (startIndex > endIndex) {
            const tempIndex = startIndex;
            startIndex = endIndex;
            endIndex = tempIndex;
        }
        return [startIndex, endIndex];
    }
    copyElements(start, end) {
        const [startIndex, endIndex] = this.getRange(start, end);
        const slicedElements = [];
        const startElement = this.elements[startIndex];
        const endElement = this.elements[endIndex];
        slicedElements.push(Object.assign(Object.assign({}, startElement), { value: startElement.value.slice(start.offset) }));
        for (let i = startIndex + 1; i < endIndex; i++) {
            slicedElements.push(this.elements[i]);
        }
        slicedElements.push(Object.assign(Object.assign({}, endElement), { value: endElement.value.slice(0, end.offset) }));
        return convertObjectListToText(slicedElements, 'plainText');
    }
    deleteElements(start, end, value, isEnabledInputAdder = true) {
        const [startIndex, endIndex] = this.getRange(start, end);
        const startElement = Object.assign({}, this.elements[startIndex]);
        const endElement = Object.assign({}, this.elements[endIndex]);
        if (value === SLASH && isEnabledInputAdder) {
            startElement.value = startElement.value.slice(0, start.offset);
            endElement.value = endElement.value.slice(end.offset);
            this.elements.splice(startIndex, endIndex - startIndex + 1, startElement, newInput('/'), endElement);
            return { index: startIndex + 1, offset: 1 };
        }
        else {
            startElement.value = `${startElement.value.slice(0, start.offset)}${value}${endElement.value.slice(end.offset)}`;
            this.elements.splice(startIndex, endIndex - startIndex + 1, startElement);
        }
        return { index: startIndex, offset: start.offset + value.length };
    }
    deletePlaceholder(elementId) {
        const index = this.elements.findIndex((item) => item.id === elementId);
        const prevElement = this.elements[index - 1];
        const nextElement = this.elements[index + 1];
        if ((prevElement === null || prevElement === void 0 ? void 0 : prevElement.type) === 'text' && (nextElement === null || nextElement === void 0 ? void 0 : nextElement.type) === 'text') {
            const caretPosition = prevElement.value.length;
            prevElement.value += nextElement.value;
            this.elements.splice(index, 2);
            return { index: index - 1, offset: caretPosition };
        }
        else if ((prevElement === null || prevElement === void 0 ? void 0 : prevElement.type) === 'text') {
            this.elements.splice(index, 1);
            return { index: index - 1, offset: prevElement.value.length };
        }
        else if ((nextElement === null || nextElement === void 0 ? void 0 : nextElement.type) === 'text') {
            this.elements.splice(index, 1);
            return { index: index + 1, offset: nextElement.value.length };
        }
    }
    deleteInput(elementId) {
        const index = this.elements.findIndex((item) => elementId === item.id);
        if (index === -1) {
            return;
        }
        const prevElement = this.elements[index - 1];
        const currElement = this.elements[index];
        const nextElement = this.elements[index + 1];
        if ((prevElement === null || prevElement === void 0 ? void 0 : prevElement.type) === 'text' && (nextElement === null || nextElement === void 0 ? void 0 : nextElement.type) === 'text') {
            const caretPosition = prevElement.value.length;
            prevElement.value = prevElement.value + nextElement.value;
            this.elements.splice(index, 2);
            return { index: index - 1, offset: caretPosition };
        }
        else if ((prevElement === null || prevElement === void 0 ? void 0 : prevElement.type) === 'text') {
            this.elements.splice(index, 1);
            return { index: index - 1, offset: prevElement.value.length };
        }
        else if ((nextElement === null || nextElement === void 0 ? void 0 : nextElement.type) === 'text') {
            this.elements.splice(index, 1);
            return { index: index, offset: 0 };
        }
        else {
            this.elements[index] = Object.assign(Object.assign({}, currElement), { value: '', type: 'text' });
            return { index: index, offset: 0 };
        }
    }
    mergeInput(elementId) {
        const index = this.elements.findIndex((item) => item.id === elementId);
        const currElement = this.elements[index];
        const prevElement = this.elements[index - 1];
        const nextElement = this.elements[index + 1];
        const result = newText(currElement.value + ' ');
        let startIndex = index;
        let deleteCount = 1;
        let caretPosition = result.value.length;
        if ((prevElement === null || prevElement === void 0 ? void 0 : prevElement.type) === 'text') {
            result.value = `${prevElement.value}${result.value}`;
            startIndex -= 1;
            deleteCount += 1;
            caretPosition = result.value.length;
            result.id = prevElement.id;
        }
        if ((nextElement === null || nextElement === void 0 ? void 0 : nextElement.type) === 'text') {
            result.value = `${result.value}${nextElement.value}`;
            deleteCount += 1;
        }
        this.elements = [
            ...this.elements.slice(0, startIndex),
            result,
            ...this.elements.slice(startIndex + deleteCount),
        ];
        return { index: startIndex, offset: caretPosition };
    }
    createInput(elementId, selection) {
        const index = this.elements.findIndex((item) => item.id === elementId);
        const element = this.elements[index];
        const input = newInput('/');
        const nextText = newText(element.value.slice(selection.endOffset));
        this.elements[index].value = this.elements[index].value.slice(0, selection.startOffset);
        this.elements = [
            ...this.elements.slice(0, index + 1),
            input,
            nextText,
            ...this.elements.slice(index + 1),
        ];
        return { index: index + 1, offset: 1 };
    }
}
export default TextBlockStore;
